import React, { useEffect, useState } from 'react';
import { KidButton } from './KidUI';
import { useSheet } from '../../hooks/useSheet';

/**
 * A calm confirm dialog in the kid style (instead of window.confirm). The confirm action may be
 * async; errors are shown inside the dialog. Escape or the backdrop cancels.
 */
const ConfirmDialog: React.FC<{
  open: boolean;
  title: string;
  message?: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}> = ({ open, title, message, confirmLabel, cancelLabel, onConfirm, onCancel }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useSheet(open);

  useEffect(() => {
    if (!open) return;
    setErr('');
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const confirm = async () => {
    setBusy(true);
    setErr('');
    try {
      await onConfirm();
    } catch (e: any) {
      setErr(e?.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10060] flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={() => !busy && onCancel()}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-kid-card bg-kid-page dark:bg-kid-bg-dark text-kid-text dark:text-kid-text-dark border-2 border-kid-border dark:border-kid-border-dark p-5 space-y-4"
      >
        <h2 id="confirm-dialog-title" className="font-kid-heading font-extrabold text-2xl">{title}</h2>
        {message && <div className="text-base">{message}</div>}
        {err && <p role="alert" className="font-bold text-red-700 dark:text-red-300">{err}</p>}
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <KidButton size="md" variant="secondary" className="flex-1" disabled={busy} onClick={onCancel}>{cancelLabel}</KidButton>
          <KidButton size="md" className="flex-1" disabled={busy} onClick={confirm}>{busy ? '…' : confirmLabel}</KidButton>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
